import React from 'react'
import './access-policy-editor.scss'
import { classnames } from '@halo/_utils/classnames'
import { Well, Drawer } from '@pushly/aqe/lib/components'
import { Form, Input, Switch, Select, Radio } from 'antd'
import { IAccessPolicy } from '@halo/interfaces/access-policy'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import { useForm } from 'antd/lib/form/Form'
import * as deepEqual from 'react-fast-compare'
import { simpleFormErrorNotification } from '@halo/_utils/notifications'

interface IEditorProps {
    accessPolicy: IAccessPolicy
    onClose: () => unknown
    onSubmit: (values: Partial<IAccessPolicy>, accessPolicy: IAccessPolicy) => unknown
    visible: boolean
}

const AccessPolicyEditor: React.FunctionComponent<IEditorProps> = ({ accessPolicy, ...props }) => {
    const [form] = useForm()

    const currValues = {
        name: accessPolicy.name,
        description: accessPolicy.description ?? null,
        effect: accessPolicy.effect,
        subjects: accessPolicy.subjects,
        actions: accessPolicy.actions,
        is_active: accessPolicy.is_active,
    }

    const [initialFormValues, setInitialFormValues] = React.useState(currValues)
    const [submitDisabled, setSubmitDisabled] = React.useState(true)

    /**
     * if the passed accessPolicy values change the form should reset
     * and update initial values for submit btn state observation
     */
    React.useEffect(() => {
        if (!deepEqual(initialFormValues, currValues)) {
            setSubmitDisabled(true)
            setInitialFormValues(currValues)
            form.setFieldsValue(currValues)
        }
    }, [...Object.values(currValues)])

    return (
        <Drawer
            className={classnames('access-policy-editor-drawer')}
            title={accessPolicy.id ? `Edit: ${accessPolicy.name}` : 'New Access Policy'}
            onClose={props.onClose}
            onSubmit={async () => {
                try {
                    await form.validateFields()
                    const formValues = form.getFieldsValue()

                    const changedValues: any = {}
                    for (const key of Object.keys(formValues) as Array<keyof IAccessPolicy>) {
                        if (formValues[key] !== accessPolicy[key]) {
                            changedValues[key] = formValues[key]
                            if (key === 'description' && !formValues[key]) {
                                changedValues[key] = null
                            } else {
                                changedValues[key] = formValues[key]
                            }
                        }
                    }

                    props.onSubmit(changedValues, accessPolicy)
                } catch (err) {
                    simpleFormErrorNotification(err)
                }
            }}
            disableSubmit={submitDisabled}
            visible={props.visible ?? false}
            getContainer={false}
            destroyOnClose={true}
        >
            <Well title="Access Policy Details" mode="ghost" hideFooter={true}>
                <Form
                    {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                    form={form}
                    initialValues={initialFormValues}
                    onValuesChange={(_fieldKV, formKV) => setSubmitDisabled(deepEqual(formKV, initialFormValues))}
                >
                    <Form.Item
                        name="name"
                        label="Name"
                        required={true}
                        rules={[{ min: 3, message: 'Name must be at least 3 characters' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="description" label="Description">
                        <Input />
                    </Form.Item>
                    <Form.Item name="effect" label="Effect">
                        <Radio.Group size="small" buttonStyle="solid">
                            <Radio.Button value="allow">allow</Radio.Button>
                            <Radio.Button value="deny">deny</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item name="subjects" label="Subjects" required={true}>
                        <Select mode="tags" />
                    </Form.Item>
                    <Form.Item name="actions" label="Actions" required={true}>
                        <Select mode="multiple">
                            <Select.Option value="manage">manage</Select.Option>
                            <Select.Option value="create">create</Select.Option>
                            <Select.Option value="read">read</Select.Option>
                            <Select.Option value="update">update</Select.Option>
                            <Select.Option value="destroy">destroy</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="is_active" label="Enabled" valuePropName="checked">
                        <Switch size="small" />
                    </Form.Item>
                </Form>
            </Well>
        </Drawer>
    )
}

export default AccessPolicyEditor
